import { safeSessionStorage } from './safeStorage';
import { safeReturnPath } from './returnTo';

export const RETURN_CONTEXT_KEY = 'verix.accounting.return.v1';
const TTL = 8 * 60 * 60 * 1000;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const HISTORY_KEY = /^[A-Za-z0-9_-]{1,64}$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const COMMON = ['date_from', 'date_to', 'presentation_currency'];
const FIELDS = {
  '/accounting/ledger': [...COMMON, 'account', 'account_exact', 'projectId', 'costCenterId', 'entry_number', 'page', 'page_size', 'open_account', 'match_status'],
  '/accounting/journal': [...COMMON, 'from', 'to', 'account', 'view', 'lens', 'page', 'page_size', 'sort', 'sort_dir'],
  '/accounting/balance-sheet': ['date_to', 'model', 'comparison', 'depth', 'include_zero', 'project', 'cost_center', 'presentation_currency'],
  '/accounting/profit-loss': [...COMMON, 'from', 'to', 'startDate', 'endDate', 'period', 'comparison', 'depth', 'include_zero', 'project', 'cost_center'],
};
const ENUMS = {
  view: ['asientos', 'por-registrar'], lens: ['all', 'ventas', 'compras', 'gastos', 'nomina', 'ajustes', 'descuadrados'],
  comparison: ['none', 'previous_year', 'previous_period'], model: ['normal', 'abreviado', 'pyme'],
  sort: ['date', 'entry_number', 'total_debe', 'total_haber'], sort_dir: ['asc', 'desc'],
  period: ['month', 'quarter', 'year', 'custom'], match_status: ['all', 'matched', 'unmatched'],
};
// `return` admite Contabilidad y el panel de inicio (DT-06). `/home` solo usa el `return`
// básico: el contexto ampliado indexa `FIELDS[path]` y no tiene entrada para `/home`.
const RETURN_ORIGINS = ['/accounting', '/home'];
const HOME_ORIGIN = ['/home'];
const homeReturn = raw => safeReturnPath(raw, HOME_ORIGIN);
const scopeOK = scope => !!scope?.userId && !!scope?.companyId;
const sameScope = (a, b) => a?.userId === b?.userId && a?.companyId === b?.companyId;
function validFilter(key, value) {
  if (typeof value !== 'string') return false;
  if (['projectId', 'costCenterId', 'project', 'cost_center'].includes(key)) return UUID.test(value);
  if (['date_from', 'date_to', 'from', 'to', 'startDate', 'endDate'].includes(key)) {
    if (!DATE.test(value)) return false;
    const date = new Date(value + 'T00:00:00Z');
    return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
  }
  if (['account', 'open_account'].includes(key)) return /^\d{1,20}$/.test(value);
  if (key === 'entry_number') return /^\d{4}\/\d{1,12}$/.test(value);
  if (key === 'presentation_currency') return /^[A-Z]{3}$/.test(value);
  if (['account_exact', 'include_zero'].includes(key)) return ['true', 'false'].includes(value);
  if (key === 'page_size') return ['10', '25', '50', '100'].includes(value);
  if (key === 'depth') return /^[1-8]$/.test(value);
  if (key === 'page') return /^\d{1,4}$/.test(value);
  return ENUMS[key]?.includes(value) ?? false;
}
function cleanOrigin(raw) {
  if (typeof raw !== 'string' || raw.length > 6000) return null;
  const [path, query = ''] = raw.split('?');
  if (!safeReturnPath(path, RETURN_ORIGINS)) return null;
  const params = new URLSearchParams(query);
  const filters = [];
  let incomplete = false;
  for (const [key, value] of params) {
    if (['return', 'return_ctx', 'return_lost', 'return_restore'].includes(key)) continue;
    if (FIELDS[path]?.includes(key) && validFilter(key, value) && !filters.some(([k]) => k === key)) filters.push([key, value]);
    else incomplete = true;
  }
  return { path, filters, incomplete, params };
}
function validReportTree(tree) {
  return Array.isArray(tree) && tree.length <= 200 && tree.every((pair, index) =>
    Array.isArray(pair) && pair.length === 2 && typeof pair[0] === 'string' &&
    pair[0].length <= 500 && /^[A-Za-z0-9_.-]{1,80}(\/[A-Za-z0-9_.-]{1,80}){0,15}$/.test(pair[0]) &&
    typeof pair[1] === 'boolean' && tree.findIndex(item => item?.[0] === pair[0]) === index);
}
function validRecord(record, now) {
  if (!record || !UUID.test(record.id) || record.version !== 1 || !FIELDS[record.path] ||
      !Number.isFinite(record.created) || record.created > now || now - record.created >= TTL ||
      !Array.isArray(record.filters) || record.filters.length > 20 ||
      typeof record.incomplete !== 'boolean' ||
      (record.historyKey != null && !HISTORY_KEY.test(record.historyKey)) ||
      (record.reportTree != null && (record.path !== '/accounting/balance-sheet' || !validReportTree(record.reportTree))) ||
      (record.parent !== null && !UUID.test(record.parent)) ||
      (record.parentBasic !== null && !homeReturn(record.parentBasic) && (!safeReturnPath(record.parentBasic) || cleanOrigin(record.parentBasic)?.incomplete || record.parentBasic !== routeOf(cleanOrigin(record.parentBasic))))) return false;
  const seen = new Set();
  return record.filters.every(pair => Array.isArray(pair) && pair.length === 2 &&
    FIELDS[record.path].includes(pair[0]) && !seen.has(pair[0]) && seen.add(pair[0]) && validFilter(...pair)) &&
    (record.anchor === null || /^(entry:[0-9a-f-]{36}|account:\d{1,20})$/i.test(record.anchor));
}
export function clearAccountingReturnContext() { safeSessionStorage.removeItem(RETURN_CONTEXT_KEY); }
/** Single namespace: a company/user switch invalidates the previous scope, including history. */
export function syncAccountingReturnScope(scope) {
  let envelope;
  try { envelope = JSON.parse(safeSessionStorage.getItem(RETURN_CONTEXT_KEY)); } catch { /* corrupt */ }
  if (!scopeOK(scope) || (envelope && !sameScope(envelope, scope))) clearAccountingReturnContext();
}
function read(scope, now) {
  if (!scopeOK(scope)) return [];
  try {
    const data = JSON.parse(safeSessionStorage.getItem(RETURN_CONTEXT_KEY));
    if (data?.version !== 1 || !sameScope(data, scope) || !Array.isArray(data.records) || data.records.length > 50) return [];
    return data.records.filter(record => validRecord(record, now));
  } catch { return []; }
}
function chainValid(records, id) {
  const visited = new Set();
  while (id) {
    if (visited.has(id) || visited.size >= 8) return false;
    visited.add(id);
    const record = records.find(r => r.id === id);
    if (!record) return false;
    id = record.parent;
  }
  return true;
}
function routeOf(record) {
  const query = new URLSearchParams(record.filters).toString();
  return record.path + (query ? '?' + query : '');
}
function attach(url, basic, id, lost = false) {
  // Destination comes from application routes; this function only owns the return parameters.
  const [path, query = ''] = url.split('?');
  const params = new URLSearchParams(query);
  for (const key of ['return', 'return_ctx', 'return_lost']) params.delete(key);
  if (basic) params.set('return', basic);
  if (id) params.set('return_ctx', id);
  if (lost) params.set('return_lost', '1');
  return path + (params.size ? '?' + params : '');
}
/** Keep complete supported filters in tab storage; never serialize free text or financial content. */
export function withAccountingReturn(url, origin, scope, anchor = null, now = Date.now(), reportTree = null, historyKey = null) {
  const home = homeReturn(origin);
  if (home) return attach(url, home, null);
  const source = cleanOrigin(origin);
  if (!source) return url;
  const basic = safeReturnPath(routeOf(source), RETURN_ORIGINS) ?? source.path;
  if (!scopeOK(scope) || !FIELDS[source.path]) return attach(url, basic, null,
    source.incomplete || basic !== routeOf(source) || !!anchor || source.params.has('return') || source.params.has('return_ctx') || !FIELDS[source.path]);
  const records = read(scope, now);
  const parentRaw = source.params.get('return_ctx');
  const rawParent = safeReturnPath(source.params.get('return'), RETURN_ORIGINS);
  const homeParent = homeReturn(rawParent);
  const cleanParent = rawParent && !homeParent ? cleanOrigin(rawParent) : null;
  const parentBasic = homeParent ?? (cleanParent ? (safeReturnPath(routeOf(cleanParent), RETURN_ORIGINS) ?? cleanParent.path) : null);
  const parent = UUID.test(parentRaw || '') && chainValid(records, parentRaw) ? parentRaw : null;
  let id;
  try { id = crypto.randomUUID(); } catch { return attach(url, basic, null, true); }
  const record = { version: 1, id, path: source.path, filters: source.filters, created: now,
    parent, parentBasic, anchor, historyKey: HISTORY_KEY.test(historyKey || '') ? historyKey : null,
    reportTree: source.path === '/accounting/balance-sheet' && validReportTree(reportTree) ? reportTree : null,
    incomplete: (reportTree !== null && (source.path !== '/accounting/balance-sheet' || !validReportTree(reportTree))) || source.incomplete || !!cleanParent?.incomplete || (!!parentRaw && !parent) || source.params.get('return_lost') === '1' };
  const next = [...records, record].slice(-50);
  if (!validRecord(record, now) || !chainValid(next, id)) return attach(url, basic, null, true);
  const payload = JSON.stringify({ version: 1, userId: scope.userId, companyId: scope.companyId, records: next });
  safeSessionStorage.setItem(RETURN_CONTEXT_KEY, payload);
  if (safeSessionStorage.getItem(RETURN_CONTEXT_KEY) !== payload) return attach(url, basic, null, true);
  return attach(url, basic, id);
}
/** Resolve only within the active scope. Explicit URL filters always win over stored ones. */
export function resolveAccountingReturn(params, scope, now = Date.now()) {
  const basic = safeReturnPath(params.get('return'), RETURN_ORIGINS);
  const id = params.get('return_ctx');
  const lost = params.get('return_lost') === '1';
  if (!id) return { target: basic, incomplete: lost };
  const records = read(scope, now);
  const record = records.find(r => r.id === id);
  if (!basic || !record || !chainValid(records, id) || record.path !== basic.split('?')[0]) return { target: basic, incomplete: true };
  const merged = new URLSearchParams(record.filters);
  // Validate the explicitly serialized filters as well; unknown URL parameters never enter storage.
  const explicit = cleanOrigin(basic);
  for (const [key, value] of explicit.filters) merged.set(key, value);
  let target = record.path + (merged.size ? '?' + merged : '');
  target = attach(target, record.parentBasic, record.parent, record.incomplete);
  const sep = target.includes('?') ? '&' : '?';
  return { target: target + sep + 'return_restore=' + id, incomplete: lost || record.incomplete || explicit.incomplete };
}
export function getAccountingReturnAnchor(id, scope, path, now = Date.now()) {
  const records = read(scope, now);
  const record = records.find(r => r.id === id && r.path === path);
  return record && chainValid(records, id) ? record.anchor : null;
}
/** Carry a previously validated accounting origin through invoice edit/save navigation. */
export function carryAccountingReturn(url, params) {
  const basic = safeReturnPath(params.get('return'), RETURN_ORIGINS);
  if (!basic) return url;
  const id = params.get('return_ctx');
  return attach(url, basic, UUID.test(id || '') ? id : null, params.get('return_lost') === '1');
}

/** Tree preferences are navigation state only, bound to the original report filters. */
export function getAccountingReturnTree(id, scope, origin, now = Date.now()) {
  const source = cleanOrigin(origin);
  const records = read(scope, now);
  const record = records.find(r => r.id === id && r.path === source?.path);
  if (!record || !chainValid(records, id) || !record.reportTree) return [];
  const current = new URLSearchParams(source.filters);
  if (source.incomplete || record.filters.length !== source.filters.length ||
      !record.filters.every(([key, value]) => current.get(key) === value)) return [];
  return record.reportTree;
}

/** Browser Back revisits the source history entry, which has no return_restore URL. */
export function getAccountingHistoryTree(historyKey, scope, origin, now = Date.now()) {
  if (!HISTORY_KEY.test(historyKey || '')) return [];
  const source = cleanOrigin(origin);
  const record = read(scope, now).findLast(r => r.historyKey === historyKey && r.path === source?.path && r.reportTree);
  return record ? getAccountingReturnTree(record.id, scope, origin, now) : [];
}
